if (!customElements.get("product-modal")) {
  customElements.define(
    "product-modal",
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.closeEvent = new CustomEvent("product:modal-close");
        this.openEvent = new CustomEvent("product:modal-open");
        this.fitZoomEnabled = this.classList.contains("cstm-product-media-modal");

        if (this.fitZoomEnabled) {
          this.modalContent = this.querySelector('[role="document"]');
          this.modalItems = Array.from(this.modalContent.children).filter((item) =>
            item.hasAttribute("data-media-id"),
          );
          this.modalNavigation = this.querySelector("[data-modal-navigation]");
          this.modalCurrent = this.querySelector("[data-modal-current]");
          this.modalTotal = this.querySelector("[data-modal-total]");
          this.previousButton = this.querySelector("[data-modal-previous]");
          this.nextButton = this.querySelector("[data-modal-next]");

          if (this.modalNavigation) {
            this.modalNavigation.hidden = this.modalItems.length <= 1;
            this.modalTotal.textContent = this.modalItems.length;
          }

          this.modalContent?.addEventListener("pointerup", (event) => {
            if (event.target.matches("img.is-active")) {
              event.stopPropagation();
            }
          });
          this.modalContent?.addEventListener("click", (event) => {
            if (event.target.matches("img.is-active")) {
              this.toggleZoom();
            }
          });

          [this.previousButton, this.nextButton].forEach((button) => {
            button?.addEventListener("pointerup", (event) => event.stopPropagation());
          });
          this.previousButton?.addEventListener("click", () => this.changeMedia(-1));
          this.nextButton?.addEventListener("click", () => this.changeMedia(1));
          this.addEventListener("keydown", (event) => {
            if (event.code === "ArrowLeft") {
              event.preventDefault();
              this.changeMedia(-1);
            } else if (event.code === "ArrowRight") {
              event.preventDefault();
              this.changeMedia(1);
            }
          });
        }
      }

      hide() {
        this.resetZoom();
        super.hide();
        document.dispatchEvent(this.closeEvent);
      }

      show(opener) {
        this.resetZoom();
        super.show(opener);
        this.showActiveMedia();
        document.dispatchEvent(this.openEvent);
      }

      resetZoom() {
        if (!this.fitZoomEnabled) return;

        this.classList.remove("is-zoomed");
        this.modalItems.forEach((item) => {
          if (item.nodeName == "IMG" && item.dataset.fitSizes) {
            item.sizes = item.dataset.fitSizes;
            delete item.dataset.fitSizes;
          }
        });
        this.modalContent.scrollTop = 0;
        this.modalContent.scrollLeft = 0;
      }

      toggleZoom() {
        if (!this.fitZoomEnabled) return;

        const isZoomed = this.classList.toggle("is-zoomed");
        if (!isZoomed) {
          this.resetZoom();
          return;
        }

        const activeImage = this.modalItems.find(
          (item) => item.nodeName == "IMG" && item.classList.contains("is-active"),
        );
        if (activeImage) {
          activeImage.dataset.fitSizes = activeImage.sizes;
          activeImage.sizes = "2200px";
          activeImage.loading = "eager";
          activeImage.fetchPriority = "high";
        }

        requestAnimationFrame(() => {
          this.modalContent.scrollTop = Math.max(
            (this.modalContent.scrollHeight - this.modalContent.clientHeight) / 2,
            0,
          );
          this.modalContent.scrollLeft = Math.max(
            (this.modalContent.scrollWidth - this.modalContent.clientWidth) / 2,
            0,
          );
        });
      }

      changeMedia(direction) {
        if (!this.fitZoomEnabled || this.modalItems.length <= 1) return;

        const activeIndex = this.modalItems.findIndex((item) =>
          item.classList.contains("is-active"),
        );
        const nextIndex =
          (activeIndex + direction + this.modalItems.length) % this.modalItems.length;

        this.resetZoom();
        this.modalItems.forEach((item) => item.classList.remove("is-active"));
        const nextMedia = this.modalItems[nextIndex];
        nextMedia.classList.add("is-active");
        this.updateCounter();
        this.loadActiveMedia(nextMedia);
        this.preloadAdjacentMedia(nextMedia);
      }

      updateCounter() {
        if (!this.fitZoomEnabled || !this.modalCurrent) return;

        const activeIndex = this.modalItems.findIndex((item) =>
          item.classList.contains("is-active"),
        );
        this.modalCurrent.textContent = Math.max(activeIndex + 1, 1);
      }

      loadActiveMedia(activeMedia) {
        window.pauseAllMedia();

        if (activeMedia.nodeName == "IMG") {
          activeMedia.loading = "eager";
          activeMedia.fetchPriority = "high";
          activeMedia.decode?.().catch(() => {});
          return;
        }

        const activeMediaTemplate = activeMedia.querySelector("template");
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

        if (
          activeMedia.nodeName == "DEFERRED-MEDIA" &&
          activeMediaContent &&
          activeMediaContent.querySelector(".js-youtube")
        ) {
          activeMedia.loadContent();
        }
      }

      preloadAdjacentMedia(activeMedia) {
        if (!this.fitZoomEnabled || this.modalItems.length <= 1) return;

        const activeIndex = this.modalItems.indexOf(activeMedia);
        const adjacentIndexes = [
          (activeIndex - 1 + this.modalItems.length) % this.modalItems.length,
          (activeIndex + 1) % this.modalItems.length,
        ];

        adjacentIndexes.forEach((index) => {
          const adjacentMedia = this.modalItems[index];
          const image =
            adjacentMedia.nodeName == "IMG" ? adjacentMedia : adjacentMedia.querySelector("img");

          if (!image || image.dataset.preloadRequested) return;

          image.dataset.preloadRequested = "true";
          image.loading = "eager";
          image.fetchPriority = "low";
          image.decode?.().catch(() => {});
        });
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute("data-media-id")}"])`,
        ).forEach((element) => {
          element.classList.remove("is-active");
        });
        const activeMedia = this.querySelector(
          `[data-media-id="${this.openedBy.getAttribute("data-media-id")}"]`,
        );
        const activeMediaTemplate = activeMedia.querySelector("template");
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add("is-active");

        if (this.fitZoomEnabled) {
          this.updateCounter();
          this.loadActiveMedia(activeMedia);
          this.preloadAdjacentMedia(activeMedia);
        }

        const container = this.querySelector('[role="document"]');
        if (this.fitZoomEnabled) {
          container.scrollTop = 0;
          container.scrollLeft = 0;
        } else {
          activeMedia.scrollIntoView();
          container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
        }

        if (
          activeMedia.nodeName == "DEFERRED-MEDIA" &&
          activeMediaContent &&
          activeMediaContent.querySelector(".js-youtube")
        ) {
          activeMedia.loadContent();
        }
      }
    },
  );
}
